// The shared metro on the client (docs/plans/2026-10-18-shared-metro.md): where the server has put you on the way to the
// march (a station's streets, its platform, the train), what to show for each, and your vote on the train. The station
// is an ordinary map world (main.ts loads it as it loads the city); the platform and the train replace the world.
import PLAT from './platform.json';
import { RideView, SharedRide } from './ride';
import { clock, toMin, lateText } from '../shared/journey';
import type { JourneyData, Routes } from '../shared/journey';
import { MetroMP, Line, makeLine, lateMinOf, realPerGameS, stationGates } from '../shared/train';
import type { GameMap, Vec } from '../shared/map';
import { FONT, INK } from './ui';
import { LED, dots, grid, rasterise, scrollX } from './led';

// the platform at night, the board in DHARNA 144's faces (src/client/ui.ts); the Yellow Line is the metro yellow
const NIGHT = INK.asphalt, LINE = INK.metro;

export type Where = 'station' | 'platform' | 'train' | 'game';
/** The server's status line ({t:'mp'}), see MetroRoom.statusOf. */
export interface Status {
  w: Where; stn: string; stop: number; me: number; n: number; size: number;
  /** everyone in the room in joining order, and which of them is you */
  names: string[]; you: number;
  train?: number; in?: number; s?: number; aboard?: [number, string][];
  /** seconds before a filling room is called off (the server's limit on the wait), while the train is not yet timed */
  left?: number;
  vote?: { ev: string; left: number; n: Record<string, number>; mine: string | null };
}
export interface Arrive { at: string; late: number; train: number; by: 'metro' | 'walk' }

async function getJson<T>(url: string): Promise<T> { const r = await fetch(url); if (!r.ok) throw new Error(`${url}: ${r.status}`); return (await r.json()) as T; }

/** m:ss, or a dash when there is no time to show. */
const clockOf = (s: number | null) => (s === null ? '—' : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`);

export class MetroClient {
  C: MetroMP | null = null; J: JourneyData | null = null; R: Routes | null = null; L: Line | null = null;
  private loading: Promise<void> | null = null;
  /** the station you were dropped at */
  stn: { slug: string; name: string; hi: string } | null = null;
  st: Status | null = null;
  /** when the last status came (performance.now ms), to run its clocks on between updates */
  private at = 0;
  private results: Record<string, string> = {};
  private ride: RideView | null = null;
  private platform = new PlatformView();
  arrived: Arrive | null = null;

  constructor(private send: (m: Record<string, unknown>) => void, private chime: () => void) {}

  /** the episode's config, journey and routes, fetched once */
  load(): Promise<void> {
    this.loading ??= (async () => {
      this.C = await getJson<MetroMP>('/story/s1e1.metro-mp.json');
      [this.J, this.R] = await Promise.all([getJson<JourneyData>(`/story/${this.C.episode}.journey.json`), getJson<Routes>(`/story/${this.C.episode}.routes.json`)]);
      this.L = makeLine(this.C, this.R);
    })();
    return this.loading;
  }

  get where(): Where | null { return this.st?.w ?? (this.stn ? 'station' : null); }
  /** the platform and the train are drawn in place of the world */
  covering() { return !!this.L && (this.where === 'platform' || this.where === 'train'); }

  welcome(m: any) {
    if (m.world === 'station' && m.stn) { this.stn = m.stn; this.st = null; this.arrived = null; this.results = {}; this.ride = null; void this.load(); }
    else if (m.world === 'game' && this.stn) { this.ride = null; if (this.st) this.st = { ...this.st, w: 'game' }; }
  }

  /** The metro's messages; false for anything else. */
  msg(m: any): boolean {
    if (m.t === 'mp') {
      const prev = this.st;
      if (prev?.train !== m.train) this.results = {};
      this.st = m as Status; this.at = performance.now();
      if (m.w === 'train' && !this.ride && this.L && this.J && this.R) this.ride = new RideView(this.J, this.R, this.C!.route, {
        times: () => this.times(), pick: (ev, c) => this.send({ t: 'vote', ev, c }), swap: () => {}, done: () => {}, companions: () => 0, chime: this.chime,
      }, () => this.results, this.shared());
      return true;
    }
    if (m.t === 'voted') { this.results[m.ev] = m.c; return true; }
    if (m.t === 'arrive') { this.arrived = m as Arrive; return true; }
    return false;
  }

  /** seconds since the last status */
  private since() { return Math.min(0.5, (performance.now() - this.at) / 1000); }
  /** seconds until your train's doors open at your stop, run on between updates; null before the timetable */
  doorsIn(): number | null { return this.st?.in === undefined ? null : Math.max(0, this.st.in - this.since()); }

  private shared(): SharedRide {
    return {
      line: this.L!,
      s: () => (this.st?.s ?? 0) + this.since(),
      aboard: () => this.st?.aboard ?? [],
      me: () => this.st?.me ?? -1,
      vote: () => this.st?.vote ? { ...this.st.vote, left: Math.max(0, this.st.vote.left - this.since()) } : null,
      results: () => this.results,
      clockMin: () => this.clockAt((this.st?.s ?? 0) + this.since()),
    };
  }

  /** The strip's times: this train's start and end on the clock, and how late that is for the meeting. */
  private times() {
    const L = this.L!, late = lateMinOf(this.C!, this.st?.train ?? 1);
    const end = this.clockAt(L.endS);
    return { start: this.clockAt(0), end, arrive: end, late };
  }

  /** The time of day on a train `s` seconds after it left GTB Nagar: train 1 reaches Patel Chowk at the meeting time. */
  clockAt(s: number) {
    const J = this.J!, L = this.L!;
    return toMin(J.clock.meetAt) - (L.endS - s) * realPerGameS(J, L) + lateMinOf(this.C!, this.st?.train ?? 1);
  }

  update(dt: number) { this.ride?.update(dt); this.platform.t += dt; }

  draw(g: CanvasRenderingContext2D, W: number, H: number) {
    if (this.where === 'train' && this.ride) this.ride.draw(g, W, H);
    else if (this.stn && this.L && this.st) this.platform.draw(g, W, H, this.stn, this.st, this.doorsIn(), this.L);
  }

  key(code: string) { return this.where === 'train' && !!this.ride?.key(code); }
  click(x: number, y: number) { return this.where === 'train' && !!this.ride?.click(x, y); }

  /** Your station's gates on its map (the map main.ts has loaded), world px. */
  gates(m: GameMap): Vec[] {
    const b = this.C?.boarding.find((x) => x.slug === this.stn?.slug);
    return b ? stationGates(m, b.names) : [];
  }

  /** The station's banner: where to go, and when the train comes. */
  stationBanner(walkM: number | null): [string, string] {
    const st = this.st, name = this.stn?.name ?? 'the metro', there = walkM === null ? '' : ` · ${Math.round(walkM)} m`;
    const kafila = st ? `${st.n}/${st.size}` : '';
    const left = this.doorsIn();
    if (left === null) return [`BOARD AT ${name.toUpperCase()}`, `Walk to a metro gate${there} · your kafila ${kafila} · called off in ${clockOf(st?.left ?? null)} unless everyone is in`];
    const mmss = `${Math.floor(left / 60)}:${String(Math.floor(left % 60)).padStart(2, '0')}`;
    if ((st?.train ?? 1) > 1) return [`NEXT TRAIN IN ${mmss}`, `Your kafila's train has gone · you'll be ${lateMinOf(this.C!, st!.train!)} min late · walk to a gate${there}`];
    return [`TRAIN AT ${name.toUpperCase()} IN ${mmss}`, `Walk to a metro gate${there} · your whole kafila is on this train`];
  }

  /** The arrival card: how you came in, how late, what that means; the rumour's debrief if the coach met it. */
  arrivalCard(): { line: string; stage: string; debrief: { en: string; source?: string } | null } | null {
    const a = this.arrived, J = this.J;
    if (!a || !J) return null;
    const how = a.by === 'walk' ? 'Off at Rajiv Chowk, on foot from Connaught Place' : 'Off at Patel Chowk, Gate 3';
    const line = `Train ${a.train} · ${how} · ${clock(toMin(J.clock.meetAt) + a.late)} · ${lateText(a.late)}`;
    const stage = a.by === 'walk' ? 'You walk in from Connaught Place. The kafila has already set off: catch up with it.'
      : a.train > 1 ? 'Your kafila came in on the train before. Catch up with them.' : 'Your kafila stepped off together. Gather at the gate.';
    const met = J.events.find((e) => e.kind === 'rumour' && this.results[e.id]);
    return { line, stage, debrief: met?.debrief ?? null };
  }

  reset() { this.stn = null; this.st = null; this.ride = null; this.arrived = null; this.results = {}; }

  state() { return { stn: this.stn?.slug ?? null, where: this.where, in: this.doorsIn(), train: this.st?.train ?? null, aboard: this.st?.aboard?.length ?? 0, vote: this.st?.vote ?? null, ride: this.ride?.state() ?? null, arrived: this.arrived }; }
}


type Box = { x: number; y: number; w: number; h: number };
type Stn = { name: string; hi: string };
/** the card the notice board's details move to on a phone: the board's own worn blue */
const BOARD_BLUE = '#1B3F8F';

/**
 * The platform (section 5), on the plate tools/make-platform.py packs: screen doors, a blank LED board and a blank
 * notice board. What the boards say is drawn live into the boxes it measured (src/client/platform.json), as the coach
 * does its strip and screen: the LED board carries the train (where it goes, when it comes, who is aboard), the notice
 * board the station (its name in both scripts, the line, the next stop, your kafila). On a narrow screen the plate is
 * scaled so the LED board fills the width, the floor runs on below it, and the notice board's details come as a card.
 */
export class PlatformView {
  t = 0;
  private img = new Image();
  /** the LED board's rasters, re-made only when the words change (or the fonts arrive) */
  private led = { key: '', top: null as HTMLCanvasElement | null, topCols: 0, bottom: null as HTMLCanvasElement | null, bottomCols: 0, since: 0, right: '', rightC: null as HTMLCanvasElement | null, rightCols: 0, grid: null as HTMLCanvasElement | null, gridKey: '' };

  constructor() { this.img.src = PLAT.image; }

  draw(g: CanvasRenderingContext2D, W: number, H: number, stn: Stn, st: Status, inS: number | null, L: Line) {
    g.fillStyle = NIGHT; g.fillRect(0, 0, W, H);
    const [iw, ih] = PLAT.size, ready = this.img.complete && this.img.naturalWidth > 0;
    // the scale: cover the screen, unless that would push the LED board off a narrow one
    const cover = Math.max(W / iw, H / ih), narrow = W < H;
    const k = narrow ? Math.min(cover, (W * 0.94) / (PLAT.ledBoard.w * iw)) : cover;
    const pw = iw * k, ph = ih * k;
    // centred on the boards (the LED and the notice board together), never past the plate's edge
    const focus = narrow ? PLAT.ledBoard.x + PLAT.ledBoard.w / 2 : (PLAT.ledBoard.x + PLAT.board.x + PLAT.board.w) / 2;
    const px = Math.min(0, Math.max(W - pw, W / 2 - focus * pw)), py = ph >= H ? (H - ph) / 2 : Math.min(H * 0.06, H - ph);
    const at = (b: Box): Box => ({ x: px + b.x * pw, y: py + b.y * ph, w: b.w * pw, h: b.h * ph });
    if (ready) {
      g.imageSmoothingQuality = 'high';
      // a plate shorter than the screen: the floor's colour carries on to the screen's foot and the ceiling's above it,
      // and the plate's own edges fade into them so there is no seam
      if (py > 0) { g.fillStyle = PLAT.ceiling; g.fillRect(0, 0, W, py + 1); }
      if (py + ph < H) { g.fillStyle = PLAT.floor; g.fillRect(0, py + ph - 1, W, H - py - ph + 1); }
      g.drawImage(this.img, px, py, pw, ph);
      const fade = (y0: number, y1: number, colour: string) => {
        const gr = g.createLinearGradient(0, y0, 0, y1); gr.addColorStop(0, colour + '00'); gr.addColorStop(1, colour);
        g.fillStyle = gr; g.fillRect(0, Math.min(y0, y1), W, Math.abs(y1 - y0));
      };
      if (py + ph < H) fade(py + ph * 0.86, py + ph, PLAT.floor);
      if (py > 0) fade(py + ph * 0.05, py, PLAT.ceiling);
    }

    // the train coming: its lights sweep the screen doors' glass from the tunnel's end in the last six seconds
    const coming = inS !== null && inS < 6 ? 1 - Math.max(0, inS) / 6 : 0;
    if (coming > 0) {
      const d = at(PLAT.doors), x = d.x + d.w * (1.1 - coming * 1.2);
      g.save(); g.beginPath(); g.rect(d.x, d.y, d.w, d.h); g.clip();
      const glow = g.createRadialGradient(x, d.y + d.h * 0.55, 4, x, d.y + d.h * 0.55, d.w * (0.15 + coming * 0.35));
      glow.addColorStop(0, `rgba(255,236,190,${0.75 * coming})`); glow.addColorStop(1, 'rgba(255,236,190,0)');
      g.globalCompositeOperation = 'screen'; g.fillStyle = glow; g.fillRect(d.x, d.y, d.w, d.h);
      g.restore();
    }

    this.drawLed(g, at(PLAT.led), st, inS, L);

    const board = at(PLAT.board), seen = board.x >= 0 && board.x + board.w <= W && ready;
    const info = boardLines(stn, st, L);
    if (seen) this.drawBoard(g, board, info, false);
    else {
      // the notice board is off a narrow screen: its details as a card on the floor just under the doors, clear of the
      // station minimap main.ts draws bottom-left (130 px and its label on a phone)
      const cy = Math.min(H - 14 - 130 - 36 - 150 - 14, at(PLAT.doors).y + at(PLAT.doors).h + 14);
      const card = { x: 16, y: cy, w: W - 32, h: 150 };
      g.fillStyle = INK.asphalt; g.fillRect(card.x + 5, card.y + 5, card.w, card.h);
      g.fillStyle = BOARD_BLUE; g.fillRect(card.x, card.y, card.w, card.h);
      g.strokeStyle = INK.asphalt; g.lineWidth = 3; g.strokeRect(card.x + 1.5, card.y + 1.5, card.w - 3, card.h - 3);
      this.drawBoard(g, { x: card.x + 6, y: card.y + 6, w: card.w - 12, h: card.h - 12 }, info, true);
    }
    // mind the gap, on the floor past the tactile strip
    const floorY = py + PLAT.strip[1] * ph, footY = Math.min(H - 16, floorY + Math.max(28, (Math.min(H, py + ph) - floorY) * 0.42));
    if (!(!seen && footY > H - 200)) {
      g.textAlign = 'center'; g.textBaseline = 'middle'; g.font = FONT.body(Math.round(Math.max(13, Math.min(18, W / 80))), 700); g.fillStyle = INK.asphalt;
      g.fillText('MIND THE GAP · कृपया दूरी बनाए रखें', W / 2, footY);
      g.textBaseline = 'alphabetic';
    }
  }

  /** The LED board, in the title's inks: the terminus and the time in metro yellow above, your kafila scrolling in newsprint below. */
  private drawLed(g: CanvasRenderingContext2D, face: Box, st: Status, inS: number | null, L: Line) {
    // the dot pitch sets two rows in about four fifths of the face, centred, clear of its red dots (fractional: whole
    // pixels would leave a third of it dark)
    const pitch = Math.max(1, Math.round(((face.h * 0.78) / (LED.rows * 2 + 3)) * 4) / 4);
    const cols = Math.floor(face.w / pitch) - 2, rowsAll = LED.rows * 2 + 3;
    const ox = Math.round(face.x + (face.w - cols * pitch) / 2), oy = Math.round(face.y + (face.h - rowsAll * pitch) / 2);
    const gk = `${cols}x${rowsAll}@${pitch}`;
    if (this.led.gridKey !== gk) { this.led.grid = grid(cols, rowsAll, pitch); this.led.gridKey = gk; this.led.key = ''; }
    g.drawImage(this.led.grid!, ox, oy);

    const end = L.stops[L.stops.length - 1], train = st.train ?? 1;
    const top = `${train > 1 ? `${train}  ` : ''}${end.hi}  ${end.name.toUpperCase()}`;
    const right = inS === null ? (st.left !== undefined ? clockOf(st.left) : `${st.n}/${st.size}`) : inS < 1 ? 'NOW' : `${Math.floor(inS / 60)}:${String(Math.floor(inS % 60)).padStart(2, '0')}`;
    const bottom = ledStatus(st, inS);
    const fonts = typeof document !== 'undefined' && document.fonts ? document.fonts.status : '';
    const key = `${top}|${bottom}|${pitch}|${fonts}`;
    if (this.led.key !== key) {
      const t = rasterise(top), b = rasterise(bottom);
      this.led.top = dots(t, INK.metro, pitch); this.led.topCols = t.cols;
      this.led.bottom = dots(b, INK.newsprint, pitch); this.led.bottomCols = b.cols;
      this.led.key = key; this.led.since = this.t; this.led.right = '';
    }
    if (this.led.right !== right + pitch + fonts) {
      const r = rasterise(right); this.led.rightC = dots(r, INK.metro, pitch); this.led.rightCols = r.cols; this.led.right = right + pitch + fonts;
    }
    g.save(); g.beginPath(); g.rect(ox, oy, cols * pitch, rowsAll * pitch); g.clip();
    // the top row: the terminus held at the left, the time at the right, never scrolled
    const room = cols - this.led.rightCols - 3;
    g.save(); g.beginPath(); g.rect(ox, oy, room * pitch, LED.rows * pitch); g.clip();
    g.drawImage(this.led.top!, ox, oy); g.restore();
    g.drawImage(this.led.rightC!, ox + (cols - this.led.rightCols) * pitch, oy);
    // the bottom row: held, then scrolled a whole LED at a time, as the coach's strip
    const x = Math.round(scrollX(this.t - this.led.since, this.led.bottomCols, cols, LED.holdS, LED.pxPerS / LED.pitch));
    g.drawImage(this.led.bottom!, ox + x * pitch, oy + (LED.rows + 3) * pitch);
    g.restore();
  }

  /**
   * The notice board, in the title's inks and faces: the Hindi name in metro yellow, the English in Bebas on newsprint
   * with a rule under it, then the facts as the UI's mono labels. Every line is measured together and the set is scaled
   * to the face with padding all round, so nothing runs onto the board's frame at any size.
   */
  private drawBoard(g: CanvasRenderingContext2D, b: Box, info: ReturnType<typeof boardLines>, card: boolean) {
    const padX = Math.max(6, b.w * (card ? 0.05 : 0.09)), padY = Math.max(5, b.h * (card ? 0.08 : 0.11));
    // each line: its size and the gap after it, in units of u
    const rows: { t: string; font: (px: number) => string; ink: string; size: number; gap: number; rule?: boolean; swatch?: boolean }[] = [
      { t: info.hi, font: (px) => FONT.hi(px, 600), ink: INK.metro, size: 1.1, gap: 0.12 },
      { t: info.name, font: (px) => FONT.display(px), ink: INK.newsprint, size: 1.5, gap: 0.55, rule: true },
      { t: info.line, font: (px) => FONT.mono(px, 600), ink: INK.metro, size: 0.6, gap: 0.42, swatch: true },
      { t: info.towards, font: (px) => FONT.mono(px, 500), ink: INK.newsprint, size: 0.6, gap: 0.36 },
      { t: info.next, font: (px) => FONT.mono(px, 500), ink: INK.newsprint, size: 0.6, gap: 0.36 },
      { t: info.kafila, font: (px) => FONT.mono(px, 700), ink: INK.metro, size: 0.6, gap: 0 },
    ];
    const units = rows.reduce((s, r) => s + r.size + r.gap, 0), u = (b.h - padY * 2) / units;
    const x = b.x + padX, w = b.w - padX * 2;
    g.textAlign = 'left'; g.textBaseline = 'bottom';
    let y = b.y + padY;
    for (const r of rows) {
      y += r.size * u;
      let tx = x, tw = w;
      if (r.swatch) { const s = r.size * u * 0.8; g.fillStyle = LINE; g.fillRect(x, y - s - r.size * u * 0.08, s, s); tx += s * 1.6; tw -= s * 1.6; }
      g.font = r.font(Math.max(8, Math.round(r.size * u * 10) / 10)); g.fillStyle = r.ink; g.fillText(r.t, tx, y, tw);
      if (r.rule) { g.fillStyle = INK.newsprint; g.globalAlpha = 0.55; g.fillRect(x, y + r.gap * u * 0.45, w, Math.max(1, u * 0.07)); g.globalAlpha = 1; }
      y += r.gap * u;
    }
    g.textBaseline = 'alphabetic';
  }
}

/** What the notice board says: the station, the line and platform, the terminus, the stop after this one, your kafila. */
export function boardLines(stn: Stn, st: Status, L: Line) {
  const end = L.stops[L.stops.length - 1], next = L.stops[st.stop + 1];
  return {
    hi: stn.hi, name: stn.name.toUpperCase(), line: 'YELLOW LINE · PLATFORM 1',
    towards: `TOWARDS ${end.name.toUpperCase()}`, next: next ? `NEXT · ${next.name.toUpperCase()}` : 'THIS TRAIN TERMINATES HERE',
    kafila: `KAFILA ${st.n}/${st.size}${st.aboard?.length ? ` · ${st.aboard.length} ABOARD` : ''}${st.left !== undefined ? ` · CALLED OFF IN ${clockOf(st.left)}` : ''}`,
  };
}

/** The LED board's scrolling line: what your kafila is waiting for, then who is already on the train. */
export function ledStatus(st: Status, inS: number | null): string {
  let s: string;
  if (inS === null) s = `Waiting for your kafila: ${st.n} of ${st.size} here. If no one else comes before the clock runs out, the kafila is called off.`;
  else if (inS < 1) s = 'Doors opening. Please mind the gap. कृपया दूरी बनाए रखें।';
  else if ((st.train ?? 1) > 1) s = 'The next train. Your kafila is on the one before.';
  else s = 'Your kafila is on this train.';
  if (st.aboard?.length) s += `  Aboard: ${st.aboard.map(([, n]) => n).join(', ')}.`;
  return s;
}
